import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, Label, TableItem } from 'components/atoms';
import { FormInput, LabeledStatus } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiOpenAiLogo, PiVinylRecord } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { GENRE, STATUS } from 'services/types';
import { useGetVideosListInfinite } from 'services/video';
import {
  IVideoSearch,
  IVideosResponse,
  TRACK_CENSORIOUS,
} from 'services/video/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getNodes } from 'utils/table/nodes.utils';

enum TRACK_COLUMNS {
  title,
  isrc,
  // source,
  language,
  genre,
  censorious,
  copyrights,
  status,
  isCreatedWithAi,
}

const videoSearchInitialValues: IVideoSearch = {
  title: '',
  isrc: '',
  ean: '',
  artistNickname: '',
  artistName: '',
};

const Videos = () => {
  usePermissionGuard('VideoPermissionsLIST**');
  const hasPermission = useHasPermission();

  const [filters, setFilters] = useState<IVideoSearch>({});

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetVideosListInfinite(filters);

  const tableData = getNodes<IVideosResponse>(data?.pages.flat());

  const { t } = useTranslation();
  const navigate = useNavigate();
  const i18nextPrefix = 'video';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const columnsVisibility = useTableColumnsVisibility('Videos3', TRACK_COLUMNS);

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/file`);
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const hasDetailPermission = hasPermission('VideoPermissionsLIST**', 16);
  const tableSelect = hasDetailPermission ? select : undefined;

  const { getSelectOptions } = useEnumToSelectOptions();

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={TRACK_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix='input.label'
                  customI18next=''
                />
              </div>
              {hasPermission('VideoPermissionsLIST**', 1) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={() => navigate('create')}>
                  <Button.Text
                    text={t(`button.create-what`, { what: t('video.what') })}
                  />
                  <Button.Icon icon={<PiVinylRecord />} />
                </Button>
              )}
            </>
          }>
          <QueryFilter<IVideoSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={videoSearchInitialValues}>
            <>
              <QueryFilter.Form<IVideoSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setFilters(values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='status'
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text name='title' label={t('input.label.title')} />
                  <FormInput.Text name='isrc' label={t('input.label.isrc')} />
                  <FormInput.Text name='ean' label={t('input.label.ean')} />
                  <FormInput.Text
                    name='artistNickname'
                    label={t('input.label.artistNickname')}
                  />
                  <FormInput.Text
                    name='artistName'
                    label={t('input.label.artistName')}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'status'
                    ? t(`enum.STATUS.${STATUS[data.value as STATUS]}`)
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        data={tableData}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        columns={[
          {
            label: t('input.label.title'),
            renderCell: ({ mainTitle }) => (
              <TableItem>
                <TableItem.Text text={mainTitle?.title} />
                {mainTitle?.version && (
                  <TableItem.Text text={mainTitle?.version} isSupplementary />
                )}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(TRACK_COLUMNS.title),
          },
          {
            label: 'ISRC',
            renderCell: ({ isrc }) => (
              <TableItem>
                <TableItem.Text text={isrc ?? undefined} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(TRACK_COLUMNS.isrc),
          },
          // {
          //   label: t('input.label.source'),
          //   renderCell: ({ source }) => (
          //     <TableItem>
          //       <TableItem.Text
          //         text={t(`enum.TRACK_SOURCE.${TRACK_SOURCE[source]}`)}
          //       />
          //     </TableItem>
          //   ),
          //   resize: true,
          //   hide: columnsVisibility.hide(TRACK_COLUMNS.source),
          // },
          {
            label: t('input.label.language'),
            renderCell: ({ language }) => (
              <TableItem>
                <TableItem.Text text={t(`enum.LANGUAGE.${language}`)} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(TRACK_COLUMNS.language),
          },
          {
            label: t('input.label.genre'),
            renderCell: ({ genre }) => (
              <TableItem>
                <TableItem.Text
                  text={genre ? genre.name : undefined}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(TRACK_COLUMNS.genre),
          },
          {
            label: t('input.label.censorious'),
            renderCell: ({ censorious }) => (
              <TableItem>
                <TableItem.Text
                  text={
                    censorious
                      ? t(
                          `enum.TRACK_CENSORIOUS.${TRACK_CENSORIOUS[censorious]}`
                        )
                      : undefined
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(TRACK_COLUMNS.censorious),
          },
          {
            label: t('input.label.copyrights'),
            renderCell: item => (
              <TableItem>
                <TableItem.Text
                  isSupplementary
                  text={
                    item.cline?.name && item.cline?.year
                      ? `© ${item.cline.year} ${item.cline.name}`
                      : undefined
                  }
                />
                <TableItem.Text
                  isSupplementary
                  text={
                    item.pline?.name && item.pline?.year
                      ? `℗ ${item.pline.year} ${item.pline.name}`
                      : undefined
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(TRACK_COLUMNS.copyrights),
          },
          {
            label: t('input.label.status'),
            renderCell: ({ videoStatus }) => (
              <TableItem justifyPosition='center'>
                <LabeledStatus status={videoStatus} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(TRACK_COLUMNS.status),
          },
          {
            label: t('input.label.isCreatedWithAi'),
            renderCell: ({ isCreatedWithAi }) => (
              <TableItem justifyPosition='center'>
                {isCreatedWithAi && (
                  <Label color='blue' size='compact'>
                    <PiOpenAiLogo /> {t('input.label.isCreatedWithAi')}
                  </Label>
                )}
              </TableItem>
            ),
            hide: columnsVisibility.hide(TRACK_COLUMNS.isCreatedWithAi),
          },
        ]}
        displayMode
      />
    </TopbarPage>
  );
};

export default Videos;
